// Utility functions shared between client and server

/**
 * Generates a unique identifier using timestamp and random components
 * Format: {timestamp}-{random}
 */
export function generateUniqueId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
}

/**
 * Generates a unique lobby ID with a specific prefix
 */
export function generateLobbyId(): string {
  return `lobby-${generateUniqueId()}`;
}

/**
 * Generates a unique player ID with a specific prefix
 */
export function generatePlayerId(): string {
  return `player-${generateUniqueId()}`;
}

/**
 * Generates a unique obstacle ID with a specific prefix
 */
export function generateObstacleId(): string {
  return `obstacle-${generateUniqueId()}`;
}

import { ValidationResult } from './types';

// Validation functions

/**
 * Validates a player pseudonym according to game rules
 * - Must be between 1 and 20 characters
 * - Cannot be only whitespace
 * - Cannot contain special characters that could break display
 */
export function validatePseudonym(pseudonym: string): ValidationResult {
  if (typeof pseudonym !== 'string') {
    return { isValid: false, error: 'Pseudonym is required' };
  }

  const trimmed = pseudonym.trim();
  
  if (trimmed.length === 0) {
    return { isValid: false, error: 'Pseudonym cannot be empty or only whitespace' };
  }

  if (trimmed.length > 20) {
    return { isValid: false, error: 'Pseudonym must be 20 characters or less' };
  }

  // Check for invalid characters (basic validation)
  const invalidChars = /[<>\"'&]/;
  if (invalidChars.test(trimmed)) {
    return { isValid: false, error: 'Pseudonym contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Validates an input event structure and timing
 */
export function validateInputEvent(event: any): ValidationResult {
  if (!event || typeof event !== 'object') {
    return { isValid: false, error: 'Input event must be an object' };
  }

  if (!event.playerId || typeof event.playerId !== 'string') {
    return { isValid: false, error: 'Input event must have a valid playerId' };
  }

  const validInputTypes = ['JUMP', 'MOVE_LEFT', 'MOVE_RIGHT'];
  if (!event.type || !validInputTypes.includes(event.type)) {
    return { isValid: false, error: 'Input event must have a valid type (JUMP, MOVE_LEFT, or MOVE_RIGHT)' };
  }

  if (typeof event.timestamp !== 'number' || event.timestamp <= 0) {
    return { isValid: false, error: 'Input event must have a valid timestamp' };
  }

  if (typeof event.sequence !== 'number' || event.sequence < 0) {
    return { isValid: false, error: 'Input event must have a valid sequence number' };
  }

  // Check timestamp is not too far in the future (prevent cheating)
  const now = Date.now();
  const maxFutureTime = 1000; // 1 second
  if (event.timestamp > now + maxFutureTime) {
    return { isValid: false, error: 'Input event timestamp is too far in the future' };
  }

  return { isValid: true };
}

/**
 * Validates game state consistency
 */
export function validateGameState(gameState: any): ValidationResult {
  if (!gameState || typeof gameState !== 'object') {
    return { isValid: false, error: 'Game state must be an object' };
  }

  if (typeof gameState.tick !== 'number' || gameState.tick < 0) {
    return { isValid: false, error: 'Game state must have a valid tick number' };
  }

  if (typeof gameState.worldSpeed !== 'number' || gameState.worldSpeed < 0) {
    return { isValid: false, error: 'Game state must have a valid world speed' };
  }

  if (typeof gameState.difficulty !== 'number' || gameState.difficulty < 0) {
    return { isValid: false, error: 'Game state must have a valid difficulty' };
  }

  if (!Array.isArray(gameState.players)) {
    return { isValid: false, error: 'Game state must have a players array' };
  }

  if (!Array.isArray(gameState.obstacles)) {
    return { isValid: false, error: 'Game state must have an obstacles array' };
  }

  if (typeof gameState.isActive !== 'boolean') {
    return { isValid: false, error: 'Game state must have a valid isActive boolean' };
  }

  // Validate each player
  for (const player of gameState.players) {
    const playerValidation = validatePlayer(player);
    if (!playerValidation.isValid) {
      return { isValid: false, error: `Invalid player: ${playerValidation.error}` };
    }
  }

  // Validate each obstacle
  for (const obstacle of gameState.obstacles) {
    const obstacleValidation = validateObstacle(obstacle);
    if (!obstacleValidation.isValid) {
      return { isValid: false, error: `Invalid obstacle: ${obstacleValidation.error}` };
    }
  }

  return { isValid: true };
}

/**
 * Validates a player object
 */
export function validatePlayer(player: any): ValidationResult {
  if (!player || typeof player !== 'object') {
    return { isValid: false, error: 'Player must be an object' };
  }

  if (!player.id || typeof player.id !== 'string') {
    return { isValid: false, error: 'Player must have a valid id' };
  }

  if (!player.pseudonym || typeof player.pseudonym !== 'string') {
    return { isValid: false, error: 'Player must have a valid pseudonym' };
  }

  const pseudonymValidation = validatePseudonym(player.pseudonym);
  if (!pseudonymValidation.isValid) {
    return pseudonymValidation;
  }

  if (!player.position || typeof player.position.x !== 'number' || typeof player.position.y !== 'number') {
    return { isValid: false, error: 'Player must have a valid position' };
  }

  if (!player.velocity || typeof player.velocity.x !== 'number' || typeof player.velocity.y !== 'number') {
    return { isValid: false, error: 'Player must have a valid velocity' };
  }

  if (typeof player.isAlive !== 'boolean') {
    return { isValid: false, error: 'Player must have a valid isAlive boolean' };
  }

  if (typeof player.isGrounded !== 'boolean') {
    return { isValid: false, error: 'Player must have a valid isGrounded boolean' };
  }

  if (typeof player.lastInputTimestamp !== 'number' || player.lastInputTimestamp < 0) {
    return { isValid: false, error: 'Player must have a valid lastInputTimestamp' };
  }

  return { isValid: true };
}

/**
 * Validates an obstacle object
 */
export function validateObstacle(obstacle: any): ValidationResult {
  if (!obstacle || typeof obstacle !== 'object') {
    return { isValid: false, error: 'Obstacle must be an object' };
  }

  if (!obstacle.id || typeof obstacle.id !== 'string') {
    return { isValid: false, error: 'Obstacle must have a valid id' };
  }

  if (!obstacle.position || typeof obstacle.position.x !== 'number' || typeof obstacle.position.y !== 'number') {
    return { isValid: false, error: 'Obstacle must have a valid position' };
  }

  if (!obstacle.dimensions || typeof obstacle.dimensions.x !== 'number' || typeof obstacle.dimensions.y !== 'number') {
    return { isValid: false, error: 'Obstacle must have valid dimensions' };
  }

  if (obstacle.dimensions.x <= 0 || obstacle.dimensions.y <= 0) {
    return { isValid: false, error: 'Obstacle dimensions must be positive' };
  }

  const validTypes = ['SPIKE', 'WALL', 'PIT'];
  if (!validTypes.includes(obstacle.type)) {
    return { isValid: false, error: 'Obstacle must have a valid type' };
  }

  if (typeof obstacle.spawnTick !== 'number' || obstacle.spawnTick < 0) {
    return { isValid: false, error: 'Obstacle must have a valid spawnTick' };
  }

  return { isValid: true };
}

// URL and routing utilities

/**
 * Generates a shareable lobby URL
 * Requirements: 1.2, 1.4
 */
export function generateLobbyUrl(baseUrl: string, lobbyId: string): string {
  // Ensure baseUrl doesn't end with slash
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  return `${cleanBaseUrl}/lobby/${lobbyId}`;
}

/**
 * Extracts lobby ID from a lobby URL
 * Requirements: 1.4
 */
export function extractLobbyIdFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    
    // Expected format: /lobby/{lobbyId}
    if (pathParts.length >= 3 && pathParts[1] === 'lobby') {
      const lobbyId = pathParts[2];
      return lobbyId && lobbyId.length > 0 ? lobbyId : null;
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Validates a lobby URL format with enhanced error messages
 * Requirements: 1.4
 */
export function validateLobbyUrl(url: string): ValidationResult {
  if (typeof url !== 'string') {
    return { isValid: false, error: 'URL must be a string' };
  }

  if (url.trim().length === 0) {
    return { isValid: false, error: 'URL cannot be empty' };
  }

  try {
    const urlObj = new URL(url);
    
    // Check if it's a valid HTTP/HTTPS URL
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' };
    }

    // Extract lobby ID
    const lobbyId = extractLobbyIdFromUrl(url);
    if (!lobbyId) {
      return { isValid: false, error: 'URL does not contain a valid lobby ID' };
    }

    // Enhanced lobby ID validation
    const lobbyIdValidation = validateLobbyIdFormat(lobbyId);
    if (!lobbyIdValidation.isValid) {
      return lobbyIdValidation;
    }

    return { isValid: true };
  } catch (error) {
    if (error instanceof TypeError) {
      return { isValid: false, error: 'Invalid URL format. Please check the URL and try again.' };
    }
    return { isValid: false, error: 'Invalid URL format' };
  }
}

/**
 * Validates lobby ID format with detailed error messages
 */
export function validateLobbyIdFormat(lobbyId: string): ValidationResult {
  if (typeof lobbyId !== 'string') {
    return { isValid: false, error: 'Lobby ID must be a string' };
  }

  if (lobbyId.trim().length === 0) {
    return { isValid: false, error: 'Lobby ID cannot be empty' };
  }

  // Check minimum length
  if (lobbyId.length < 5) {
    return { isValid: false, error: 'Lobby ID is too short' };
  }

  // Check maximum length to prevent abuse
  if (lobbyId.length > 100) {
    return { isValid: false, error: 'Lobby ID is too long' };
  }

  // Check for invalid characters that could cause security issues
  const invalidChars = /[<>\"'&\s]/;
  if (invalidChars.test(lobbyId)) {
    return { isValid: false, error: 'Lobby ID contains invalid characters' };
  }

  // Check for suspicious patterns
  if (lobbyId.includes('..') || lobbyId.includes('//')) {
    return { isValid: false, error: 'Lobby ID contains invalid patterns' };
  }

  // Accept both UUID format and lobby-{timestamp}-{random} format
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const lobbyPattern = /^lobby-[a-z0-9]+-[a-z0-9]+$/;
  
  if (uuidPattern.test(lobbyId)) {
    // Valid UUID format
    return { isValid: true };
  }
  
  if (lobbyPattern.test(lobbyId)) {
    // Valid lobby-{timestamp}-{random} format
    return { isValid: true };
  }
  
  return { isValid: false, error: 'Invalid lobby ID format' };

  return { isValid: true };
}

/**
 * Parses request path to determine routing action
 * Requirements: 1.4
 */
export function parseRequestPath(pathname: string): { action: string; lobbyId?: string } {
  const pathParts = pathname.split('/').filter(part => part.length > 0);
  
  if (pathParts.length === 0) {
    return { action: 'homepage' };
  }
  
  if (pathParts[0] === 'lobby' && pathParts.length >= 2) {
    const lobbyId = pathParts[1];
    if (lobbyId && lobbyId.length > 0) {
      return { action: 'join-lobby', lobbyId: lobbyId };
    }
    return { action: 'not-found' };
  }
  
  if (pathParts[0] === 'api') {
    if (pathParts.length >= 2) {
      return { action: `api-${pathParts[1]}` };
    }
    return { action: 'api' };
  }
  
  return { action: 'not-found' };
}